<template>
  <div class="non-certificate-report">
    <!-- 세그먼트 헤더 -->
    <a-alert
      message="🎯 신규 고객 (인증서 미보유자)"
      description="목적: 신규 고객 획득 (예약 전환) | 경로: 친구톡 → 퀴즈 참여 → 병원 예약"
      type="success"
      show-icon
      class="segment-header"
    />

    <!-- 핵심 지표 카드 -->
    <a-row :gutter="[16, 16]" class="metrics-section">
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card">
          <a-statistic
            title="퀴즈 참여자"
            :value="metrics.totalQuizParticipants"
            :value-style="{ color: '#1890ff', fontSize: '24px' }"
          >
            <template #prefix><SolutionOutlined /></template>
          </a-statistic>
          <div class="metric-detail">eventattendees 생성 기준</div>
          <div v-if="hasThermageData" class="metric-thermage">
            써마지: {{ formatNumber(thermageMetrics.totalQuizParticipants) }} ({{ formatPercentage(thermageMetrics.totalQuizParticipants / metrics.totalQuizParticipants * 100) }})
          </div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card">
          <a-statistic
            title="병원 선택"
            :value="metrics.totalHospitalSelections"
            :value-style="{ color: '#52c41a', fontSize: '24px' }"
          >
            <template #prefix><ShopOutlined /></template>
          </a-statistic>
          <div class="metric-detail">{{ formatPercentage(metrics.hospitalSelectionRate) }} 선택률</div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card">
          <a-statistic
            title="병원 예약"
            :value="metrics.totalReservations"
            :value-style="{ color: '#f5222d', fontSize: '24px' }"
          >
            <template #prefix><CalendarOutlined /></template>
          </a-statistic>
          <div class="metric-detail highlight-text">최종 목표 지표</div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card class="metric-card">
          <a-statistic
            title="예약 전환율"
            :value="metrics.reservationConversionRate"
            suffix="%"
            :precision="2"
            :value-style="{ color: '#faad14', fontSize: '24px' }"
          >
            <template #prefix><RiseOutlined /></template>
          </a-statistic>
          <div class="metric-detail">퀴즈 참여 → 예약</div>
        </a-card>
      </a-col>
    </a-row>

    <!-- 예약 전환 퍼널 -->
    <a-card title="🎯 병원 예약 전환 퍼널" class="funnel-card">
      <div class="funnel-visualization-v2">
        <a-row :gutter="[16, 20]">
          <a-col :span="24" v-for="(step, index) in reservationFunnel" :key="index">
            <div class="funnel-step-item">
              <div class="step-header-row">
                <div class="step-info-row">
                  <span class="step-number-badge" :style="{ backgroundColor: step.color }">{{ index + 1 }}</span>
                  <span class="step-label-text">{{ step.label }}</span>
                </div>
                <div class="step-metrics-row">
                  <span class="step-value-text">{{ formatNumber(step.value) }}</span>
                  <span class="step-rate-text" v-if="index > 0">{{ formatPercentage(step.conversionRate) }}</span>
                </div>
              </div>
              <a-progress
                :percent="Math.round(step.percentage)"
                :stroke-color="step.color"
                :show-info="false"
                :stroke-width="12"
              />
            </div>
            <div class="funnel-arrow-down" v-if="index < reservationFunnel.length - 1">
              <DownOutlined :style="{ fontSize: '18px', color: '#d9d9d9' }" />
            </div>
          </a-col>
        </a-row>
      </div>

      <!-- 주요 전환율 요약 -->
      <a-row :gutter="16" class="conversion-summary">
        <a-col :span="8">
          <div class="conversion-item">
            <div class="conversion-label">퀴즈 참여율</div>
            <div class="conversion-value primary">{{ formatPercentage(metrics.quizParticipationRate) }}</div>
          </div>
        </a-col>
        <a-col :span="8">
          <div class="conversion-item">
            <div class="conversion-label">병원 선택율</div>
            <div class="conversion-value">{{ formatPercentage(metrics.hospitalSelectionRate) }}</div>
          </div>
        </a-col>
        <a-col :span="8">
          <div class="conversion-item">
            <div class="conversion-label">예약 완료율</div>
            <div class="conversion-value success">{{ formatPercentage(metrics.reservationCompletionRate) }}</div>
          </div>
        </a-col>
      </a-row>
    </a-card>

    <!-- 바이럴 및 리워드 -->
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :lg="12">
        <a-card title="🔄 바이럴 확산" class="viral-card">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-statistic
                title="공유자 수"
                :value="metrics.totalShares"
              >
                <template #prefix><ShareAltOutlined style="color: #faad14" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="12">
              <a-statistic
                title="공유율"
                :value="metrics.shareRate"
                suffix="%"
                :precision="2"
              >
                <template #prefix><PercentageOutlined style="color: #1890ff" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="24" style="margin-top: 16px">
              <div class="viral-insight">
                <p>신규 고객의 공유를 통한 2차 확산 효과</p>
              </div>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
      <a-col :xs="24" :lg="12">
        <a-card title="🎁 리워드 지표" class="reward-card">
          <a-row :gutter="16">
            <a-col :span="12">
              <a-statistic
                title="쿠폰 발급"
                :value="metrics.totalCoupons"
              >
                <template #prefix><GiftOutlined style="color: #f5222d" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="12">
              <a-statistic
                title="발급률"
                :value="metrics.couponRate"
                suffix="%"
                :precision="2"
              >
                <template #prefix><PercentageOutlined style="color: #1890ff" /></template>
              </a-statistic>
            </a-col>
            <a-col :span="24" style="margin-top: 16px">
              <div class="reward-detail">
                <p>• 공유 5회 쿠폰: 최대 1장</p>
                <p>• 인증서 미보유자는 1장만 발급</p>
              </div>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
    </a-row>

    <!-- 일별 상세 데이터 -->
    <a-card title="📊 일별 상세 데이터" class="detail-card">
      <a-table
        :columns="detailColumns"
        :data-source="friendData"
        :pagination="{ pageSize: 10 }"
        :scroll="{ x: 1400 }"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'date'">
            <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
          </template>
          <template v-else-if="['quizParticipants', 'hospitalSelections', 'customerInfoInputs', 'reservations', 'shares', 'couponIssuances'].includes(column.key)">
            <span :class="{ 'highlight-value': column.key === 'reservations' && record[column.key] > 0 }">
              {{ formatNumber(record[column.key]) }}
            </span>
          </template>
          <template v-else-if="column.key === 'conversionRate'">
            <span>{{ formatPercentage(record.conversionRate) }}</span>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 인사이트 -->
    <a-card title="💡 분석 인사이트" class="insight-card">
      <a-row :gutter="16">
        <a-col :xs="24" :md="8">
          <div class="insight-box">
            <div class="insight-icon">📊</div>
            <div class="insight-title">퀴즈→예약 전환율</div>
            <div class="insight-value-large">{{ formatPercentage(metrics.reservationConversionRate) }}</div>
            <div class="insight-desc">실측 데이터 기준 약 2.08%</div>
          </div>
        </a-col>
        <a-col :xs="24" :md="8">
          <div class="insight-box">
            <div class="insight-icon">🎯</div>
            <div class="insight-title">개선 포인트</div>
            <div class="insight-text">
              <p>• 병원 선택율: {{ formatPercentage(metrics.hospitalSelectionRate) }}</p>
              <p>• 예약 완료율: {{ formatPercentage(metrics.reservationCompletionRate) }}</p>
              <p class="insight-note">→ 예약 단계 UX 개선 필요</p>
            </div>
          </div>
        </a-col>
        <a-col :xs="24" :md="8">
          <div class="insight-box">
            <div class="insight-icon">🚀</div>
            <div class="insight-title">핵심 성과</div>
            <div class="insight-text">
              <p>• 총 예약: {{ formatNumber(metrics.totalReservations) }}건</p>
              <p>• 신규 고객 전환 성공</p>
              <p class="insight-note">→ 지속적인 퀴즈 참여 유도 필요</p>
            </div>
          </div>
        </a-col>
      </a-row>
    </a-card>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { Card, Row, Col, Statistic, Alert, Table, Progress } from 'ant-design-vue';
import {
  SolutionOutlined,
  ShopOutlined,
  CalendarOutlined,
  RiseOutlined,
  DownOutlined,
  ShareAltOutlined,
  GiftOutlined,
  PercentageOutlined,
} from '@ant-design/icons-vue';
import { formatNumber, formatPercentage, formatDate, getDayOfWeek } from '../utils';

export default {
  name: 'NonCertificateReport',
  components: {
    ACard: Card,
    ARow: Row,
    ACol: Col,
    AStatistic: Statistic,
    AAlert: Alert,
    ATable: Table,
    AProgress: Progress,
    SolutionOutlined,
    ShopOutlined,
    CalendarOutlined,
    RiseOutlined,
    DownOutlined,
    ShareAltOutlined,
    GiftOutlined,
    PercentageOutlined,
  },
  props: {
    reportData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    // 인증서 미보유자 데이터 필터링 (NON_CERTIFICATE 세그먼트)
    const friendData = computed(() => {
      return props.reportData
        .filter(item => item.messageType === 'friend')
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    // THERMAGE 데이터 존재 여부 확인
    const hasThermageData = computed(() => {
      return friendData.value.some(item => item.thermage);
    });

    // 핵심 지표 계산
    const metrics = computed(() => {
      const data = friendData.value;
      const totalQuizParticipants = data.reduce((sum, item) => sum + (item.quizParticipants || 0), 0);
      const totalHospitalSelections = data.reduce((sum, item) => sum + (item.hospitalSelections || 0), 0);
      const totalCustomerInfoInputs = data.reduce((sum, item) => sum + (item.customerInfoInputs || 0), 0);
      const totalReservations = data.reduce((sum, item) => sum + (item.reservations || 0), 0);
      const totalShares = data.reduce((sum, item) => sum + (item.shares || 0), 0);
      const totalCoupons = data.reduce((sum, item) => sum + (item.couponIssuances || 0), 0);
      const totalEventPageVisits = data.reduce((sum, item) => sum + (item.eventPageVisits || 0), 0);

      return {
        totalQuizParticipants,
        totalHospitalSelections,
        totalCustomerInfoInputs,
        totalReservations,
        totalShares,
        totalCoupons,
        totalEventPageVisits,
        quizParticipationRate: totalEventPageVisits > 0 ? (totalQuizParticipants / totalEventPageVisits) * 100 : 0,
        hospitalSelectionRate: totalQuizParticipants > 0 ? (totalHospitalSelections / totalQuizParticipants) * 100 : 0,
        reservationCompletionRate: totalHospitalSelections > 0 ? (totalReservations / totalHospitalSelections) * 100 : 0,
        reservationConversionRate: totalQuizParticipants > 0 ? (totalReservations / totalQuizParticipants) * 100 : 0,
        shareRate: totalQuizParticipants > 0 ? (totalShares / totalQuizParticipants) * 100 : 0,
        couponRate: totalQuizParticipants > 0 ? (totalCoupons / totalQuizParticipants) * 100 : 0,
      };
    });

    // THERMAGE 고객 지표 계산
    const thermageMetrics = computed(() => {
      if (!hasThermageData.value) return null;

      const data = friendData.value;
      const totalQuizParticipants = data.reduce((sum, item) => sum + (item.thermage?.quizParticipants || 0), 0);
      const totalHospitalSelections = data.reduce((sum, item) => sum + (item.thermage?.hospitalSelections || 0), 0);
      const totalCustomerInfoInputs = data.reduce((sum, item) => sum + (item.thermage?.customerInfoInputs || 0), 0);
      const totalReservations = data.reduce((sum, item) => sum + (item.thermage?.reservations || 0), 0);
      const totalShares = data.reduce((sum, item) => sum + (item.thermage?.shares || 0), 0);
      const totalCoupons = data.reduce((sum, item) => sum + (item.thermage?.couponIssuances || 0), 0);
      const totalEventPageVisits = data.reduce((sum, item) => sum + (item.thermage?.eventPageVisits || 0), 0);

      return {
        totalQuizParticipants,
        totalHospitalSelections,
        totalCustomerInfoInputs,
        totalReservations,
        totalShares,
        totalCoupons,
        totalEventPageVisits,
        quizParticipationRate: totalEventPageVisits > 0 ? (totalQuizParticipants / totalEventPageVisits) * 100 : 0,
        hospitalSelectionRate: totalQuizParticipants > 0 ? (totalHospitalSelections / totalQuizParticipants) * 100 : 0,
        reservationCompletionRate: totalHospitalSelections > 0 ? (totalReservations / totalHospitalSelections) * 100 : 0,
        reservationConversionRate: totalQuizParticipants > 0 ? (totalReservations / totalQuizParticipants) * 100 : 0,
        shareRate: totalQuizParticipants > 0 ? (totalShares / totalQuizParticipants) * 100 : 0,
        couponRate: totalQuizParticipants > 0 ? (totalCoupons / totalQuizParticipants) * 100 : 0,
      };
    });

    // 예약 전환 퍼널
    const reservationFunnel = computed(() => {
      const eventPageVisits = metrics.value.totalEventPageVisits;
      const quizParticipants = metrics.value.totalQuizParticipants;
      const hospitalSelections = metrics.value.totalHospitalSelections;
      const customerInfoInputs = metrics.value.totalCustomerInfoInputs;
      const reservations = metrics.value.totalReservations;

      const maxValue = quizParticipants || 1;

      return [
        {
          label: '이벤트 페이지 유입',
          value: eventPageVisits,
          percentage: eventPageVisits > quizParticipants ? 100 : (eventPageVisits / maxValue) * 100,
          conversionRate: 100,
          color: '#1890ff'
        },
        {
          label: '퀴즈 참여 (eventattendees 생성)',
          value: quizParticipants,
          percentage: 100,
          conversionRate: eventPageVisits > 0 ? (quizParticipants / eventPageVisits) * 100 : 0,
          color: '#52c41a'
        },
        {
          label: '병원 선택',
          value: hospitalSelections,
          percentage: (hospitalSelections / maxValue) * 100,
          conversionRate: quizParticipants > 0 ? (hospitalSelections / quizParticipants) * 100 : 0,
          color: '#13c2c2'
        },
        {
          label: '고객 정보 입력',
          value: customerInfoInputs,
          percentage: (customerInfoInputs / maxValue) * 100,
          conversionRate: hospitalSelections > 0 ? (customerInfoInputs / hospitalSelections) * 100 : 0,
          color: '#722ed1'
        },
        {
          label: '병원 예약 완료',
          value: reservations,
          percentage: (reservations / maxValue) * 100,
          conversionRate: customerInfoInputs > 0 ? (reservations / customerInfoInputs) * 100 : 0,
          color: '#f5222d'
        }
      ];
    });

    // 상세 테이블 컬럼
    const detailColumns = [
      { title: '날짜', dataIndex: 'date', key: 'date', width: 100, fixed: 'left' },
      { title: '퀴즈 참여', dataIndex: 'quizParticipants', key: 'quizParticipants', width: 100, align: 'right' },
      { title: '병원 선택', dataIndex: 'hospitalSelections', key: 'hospitalSelections', width: 100, align: 'right' },
      { title: '정보 입력', dataIndex: 'customerInfoInputs', key: 'customerInfoInputs', width: 100, align: 'right' },
      { title: '예약 완료', dataIndex: 'reservations', key: 'reservations', width: 100, align: 'right' },
      { title: '퀴즈→예약율', dataIndex: 'conversionRate', key: 'conversionRate', width: 120, align: 'right' },
      { title: '공유', dataIndex: 'shares', key: 'shares', width: 80, align: 'right' },
      { title: '쿠폰 발급', dataIndex: 'couponIssuances', key: 'couponIssuances', width: 100, align: 'right' },
    ];

    // 날짜 클래스
    const getDateClass = (dateStr) => {
      const dayOfWeek = getDayOfWeek(dateStr);
      if (dayOfWeek === 0) return 'date-sunday';
      if (dayOfWeek === 6) return 'date-saturday';
      return '';
    };

    return {
      friendData,
      metrics,
      thermageMetrics,
      hasThermageData,
      reservationFunnel,
      detailColumns,
      formatNumber,
      formatPercentage,
      formatDate,
      getDateClass,
    };
  },
};
</script>

<style scoped>
.non-certificate-report {
  padding: 24px;
  background: #fafafa;
}

.segment-header {
  margin-bottom: 24px;
  border-radius: 8px;
}

.metrics-section {
  margin-bottom: 24px;
}

.metric-card {
  text-align: center;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-color: #d9d9d9;
}

.metric-detail {
  margin-top: 12px;
  font-size: 13px;
  color: #8c8c8c;
  font-weight: 500;
}

.metric-thermage {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: #1890ff;
  font-weight: 600;
}

.highlight-text {
  color: #f5222d;
  font-weight: 700;
}

:deep(.metric-card .ant-statistic-title) {
  font-size: 14px;
  font-weight: 600;
  color: #595959;
}

:deep(.metric-card .ant-statistic-content-value) {
  font-weight: 700;
}

.funnel-card {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.funnel-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

.funnel-visualization-v2 {
  padding: 20px;
}

.funnel-step-item {
  background: #fafafa;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.funnel-step-item:hover {
  background: #f5f5f5;
  border-color: #d9d9d9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.step-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.step-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-number-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 13px;
}

.step-label-text {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.step-metrics-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.step-value-text {
  font-size: 20px;
  font-weight: 700;
  color: #262626;
}

.step-rate-text {
  font-size: 13px;
  font-weight: 500;
  color: #1890ff;
}

.funnel-arrow-down {
  text-align: center;
  margin: 4px 0;
}

.conversion-summary {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;
}

.conversion-item {
  text-align: center;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
}

.conversion-label {
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.conversion-value {
  font-size: 28px;
  font-weight: 700;
  color: #1890ff;
}

.conversion-value.primary {
  color: #1890ff;
}

.conversion-value.success {
  color: #52c41a;
}

.viral-card,
.reward-card {
  height: 100%;
}

.viral-insight {
  padding: 12px;
  background: #f0f5ff;
  border-radius: 6px;
  text-align: center;
  font-size: 13px;
  color: #595959;
}

.reward-detail {
  padding: 12px;
  background: #fff7e6;
  border-radius: 6px;
  font-size: 12px;
  color: #595959;
}

.reward-detail p {
  margin: 4px 0;
}

.detail-card {
  margin-top: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.detail-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

:deep(.detail-card .ant-card-body) {
  padding: 24px;
}

.highlight-value {
  color: #f5222d;
  font-weight: 700;
}

.date-saturday {
  color: #1890ff;
  font-weight: 600;
}

.date-sunday {
  color: #f5222d;
  font-weight: 600;
}

.insight-card {
  margin-top: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.insight-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

.insight-box {
  padding: 24px;
  background: #fafafa;
  border-radius: 8px;
  text-align: center;
  height: 100%;
}

.insight-icon {
  font-size: 36px;
  margin-bottom: 12px;
}

.insight-title {
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
}

.insight-value-large {
  font-size: 36px;
  font-weight: 700;
  color: #1890ff;
  margin-bottom: 8px;
}

.insight-desc {
  font-size: 12px;
  color: #8c8c8c;
}

.insight-text {
  font-size: 13px;
  color: #595959;
  text-align: left;
}

.insight-text p {
  margin: 6px 0;
}

.insight-note {
  color: #1890ff;
  font-weight: 600;
  margin-top: 8px;
}

:deep(.ant-card-head-title) {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

:deep(.viral-card .ant-card-head),
:deep(.reward-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

.viral-card,
.reward-card {
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
</style>
